
import { Component, Vue, Watch, Prop } from 'vue-property-decorator'
import { debounce } from 'lodash'
import Sortable, { SortableEvent } from 'sortablejs'

import authModule from '@/store/auth'
import listModule from '@/store/list'
import whitelabelModule from '@/store/whitelabel'
import companyModule from '@/store/company'
import venueModule from '@/store/venue'
import cardModule from '@/store/card'
import filterModal from '@/components/listView/FilterModal.vue'
import { Whitelabel } from '@/types/Whitelabel'
import { Gender } from '@/utils/enums'
import {
  ListType,
  DefaultCardList,
  DealTypeText,
  DealType
} from '@/utils/enums'
import { getKeyByValue } from '@/utils/utility-manager'
import { format } from 'date-fns'
import { Company, Venue } from '@/types/Company'
import { TextValue } from '@/types/Types'

@Component({
  name: 'ContentList',
  components: {
    filterModal
  }
})
class ContentList extends Vue {
  @Prop({ default: true }) readonly searchable!: boolean
  @Prop({ default: false }) readonly filterable!: boolean
  @Prop() readonly type!: string
  @Prop() readonly title!: string
  @Prop() readonly dialogTitle!: string
  @Prop() readonly searchPlaceholder!: string
  @Prop() readonly items!: any[]
  @Prop() readonly headers!: TextValue[]
  @Prop() readonly filters?: string[]
  @Prop() readonly storeModule!: any
  @Prop() readonly defaultFilters?: { [key: string]: any }[]

  public dialog = false
  public isLoading = true
  public sortDesc: boolean[] = []
  public sortBy: string[] = []
  public searchTerm = ''
  public currentPage = 1
  public defaultCardList = DefaultCardList
  public showDeletePrompt = false
  public currentItem = undefined

  public debounceSearch = debounce(async (context: any): Promise<void> => {
    // context = this
    context.currentPage = 1
    context.fetchAll()
  }, 1000)

  public get auth() {
    return authModule
  }

  public get tableItems() {
    if (!this.filterable || !this.whitelabel) {
      return this.items
    }

    return this.items.filter(el => el.whitelabel === this.whitelabel._id)
  }

  public get perPage(): number {
    return listModule.perPage
  }

  public set perPage(val: number) {
    listModule.setPerPage(val)
  }

  public get totalPages(): number {
    return listModule.totalPages
  }

  public set totalPages(val: number) {
    listModule.setTotalPages(val)
  }

  public get whitelabel(): Whitelabel {
    return whitelabelModule.whitelabel
  }

  public get whitelabels(): Whitelabel[] {
    return whitelabelModule.whitelabels
  }

  public get companies(): Partial<Company>[] {
    return companyModule.companies
  }

  public get venues(): Partial<Venue>[] {
    return venueModule.venues
  }

  public get isDeal(): boolean {
    return this.type === ListType.DEAL
  }

  public get isCardList(): boolean {
    return this.type === ListType.CARDLIST
  }

  public get isUser(): boolean {
    return this.type === ListType.USER
  }

  public isDateHeader(header: string): boolean {
    return header.endsWith('Date') || header === 'createdAt'
  }

  public isBadgeHeader(header: string): boolean {
    return header === 'active' || header === 'enable' || header === 'status'
  }

  public requestNewOrder(evt: any): void {
    this.sortBy = evt.sortBy
    this.sortDesc = evt.sortDesc
    this.fetchAll()
  }

  public isFeaturedCardList(name: string): boolean {
    return name === DefaultCardList.NAME && this.isCardList
  }

  public onDelete(item: any): void {
    this.showDeletePrompt = true
    this.currentItem = item
  }

  public onDeleteConfirmed(): void {
    this.showDeletePrompt = false
    this.$emit('deleteRow', this.currentItem!)
  }

  public onDeleteCancelled(): void {
    this.showDeletePrompt = false
    this.currentItem = undefined
  }

  public formattedDealType(value: any): string {
    if (Array.isArray(value)) {
      return value
        .map(singleValue => DealTypeText[getKeyByValue(DealType, singleValue)])
        .join(', ')
    }

    return DealTypeText[getKeyByValue(DealType, value)]
  }

  public formattedDate(date: any): string | undefined {
    if (!date) return undefined
    return format(new Date(date), 'dd.MM.yyyy')
  }

  public formattedGender(gender: number): string {
    if (gender === Gender.FEMALE) {
      return 'Weiblich'
    } else if (gender === Gender.MALE) {
      return 'Männlich'
    }
    return 'Divers'
  }

  @Watch('tableItems')
  public createSortables(): void {
    if (!this.whitelabel || !this.whitelabel._id) return
    const contentList = document.getElementById('content-list')!
    if (!(contentList instanceof HTMLElement)) return
    // Makes the list of waypoints rearrangable per drag and drop
    Sortable.create(contentList, {
      draggable: '.item', // Looks for element with class="item"
      handle: '.handle', // Looks for element with class="handle"
      animation: 150,
      onEnd: (evt: SortableEvent) => {
        if (evt.oldIndex === evt.newIndex || this.type !== ListType.CARDLIST) {
          return
        }
        this.tableItems.splice(
          evt.newIndex!,
          0,
          this.tableItems.splice(evt.oldIndex!, 1)[0]
        )

        const activeLists: string[] = this.tableItems
          .filter(el => el.active === true)
          .map(el => el._id)

        cardModule.rearrangeActiveLists({
          whitelabel: this.whitelabel._id,
          cardLists: activeLists
        })
      }
    })
  }

  @Watch('searchTerm')
  public searchHandler(): void {
    this.debounceSearch(this)
  }

  @Watch('currentPage')
  public paginationHandler(): void {
    this.$router.push({
      name: this.$route.name!,
      params: { page: this.currentPage.toString() }
    })
    this.fetchAll()
  }

  public async created(): Promise<void> {
    this.isLoading = true
    this.currentPage = parseInt(this.$route.params.page) || 1
    if (this.type === ListType.ADMIN) {
      this.totalPages = await this.storeModule.fetchAllAdmins({
        limit: this.perPage
      })
      this.isLoading = false
      return
    }
    if (this.type === ListType.PARTNER) {
      this.totalPages = await this.storeModule.fetchAllPartners({
        limit: this.perPage
      })
      this.isLoading = false
      return
    }
    if (this.isCardList) {
      await this.storeModule.fetchAllCardLists({ limit: 0 })
      this.isLoading = false
      return
    }
    this.totalPages = await this.storeModule.fetchAll({
      limit: this.perPage
    })

    this.isLoading = false
  }

  public async fetchAll(): Promise<void> {
    this.isLoading = true

    const params = {
      limit: this.perPage,
      q: this.searchTerm,
      page: this.currentPage,
      sortBy: this.sortBy.toString(),
      sortDesc: this.sortDesc.toString()
    }

    if (this.isCardList) {
      params.limit = 0
      this.totalPages = await this.storeModule.fetchAllCardLists(params)
    } else {
      this.totalPages = await this.storeModule.fetchAll(params)
    }

    this.isLoading = false
  }

  public mounted(): void {
    this.createSortables()
  }

  public getWhitelabelByID(id: string): string {
    const wl = this.whitelabels.find(el => el._id === id)
    if (wl) {
      return wl.name
    }
    return ''
  }

  public hasAcceptedInvitation(item: any): boolean {
    const { createdAt, lastLogin } = item

    if (typeof createdAt !== 'string' || typeof lastLogin !== 'string')
      return false

    return createdAt.slice(0, -5) !== lastLogin.slice(0, -5)
  }
}

export default ContentList
